import React, { useState } from "react";
import {
  Pencil, UserCog, CalendarClock, Copy, Share2, Trash2, Check, X, Loader2,
} from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { MemberAvatar } from "@/components/Atoms";
import { api } from "@/lib/api";
import { localYmd } from "@/lib/format";
import { shareWithIntro } from "@/lib/share";

/**
 * ItemActionSheet — bottom-sheet of contextual actions for a chore or todo.
 *
 * Opens on long-press of any row. Surfaces the workhorse actions families
 * actually need: rename, reassign, reschedule, duplicate, share, delete.
 *
 * Props:
 *   open: bool
 *   kind: "todo" | "chore"
 *   item: the row object (must have id + title; for todo: assignee_person_id,
 *         due_at; for chore: assignee_person_ids, due_at)
 *   familyId, listId (todos only), members
 *   onChanged: () => void   — caller refetches its list
 *   onClose: () => void
 */
export function ItemActionSheet({ open, kind, item, familyId, listId, listName, members = [], onChanged, onClose }) {
  const [mode, setMode] = useState("menu"); // menu | rename | reassign | reschedule
  const [busy, setBusy] = useState(false);

  if (!open || !item) return null;

  const reset = () => { setMode("menu"); setBusy(false); };
  const close = () => { reset(); onClose?.(); };
  const fin = () => { reset(); onChanged?.(); onClose?.(); };

  const patchUrl = kind === "chore"
    ? `/families/${familyId}/chores/${item.id}`
    : `/families/${familyId}/todo-lists/${listId}/items/${item.id}`;
  const deleteUrl = kind === "chore"
    ? `/families/${familyId}/chores/${item.id}`
    : `/families/${familyId}/todo-lists/${listId}/items/${item.id}`;

  const doRename = async (title) => {
    setBusy(true);
    try { await api.patch(patchUrl, { title }); fin(); }
    finally { setBusy(false); }
  };

  const doReassign = async (personId) => {
    setBusy(true);
    try {
      const body = kind === "chore"
        ? { assignee_person_ids: personId ? [personId] : [] }
        : { assignee_person_id: personId || null };
      await api.patch(patchUrl, body);
      fin();
    } finally { setBusy(false); }
  };

  const doReschedule = async (iso) => {
    setBusy(true);
    try { await api.patch(patchUrl, { due_at: iso }); fin(); }
    finally { setBusy(false); }
  };

  const doDuplicate = async () => {
    setBusy(true);
    try {
      if (kind === "chore") {
        await api.post(`/families/${familyId}/chores`, {
          title: item.title,
          description: item.description || null,
          assignee_person_ids: item.assignee_person_ids || [],
          due_at: item.due_at || null,
          reward_stars: item.reward?.amount || null,
        });
      } else {
        await api.post(`/families/${familyId}/todo-lists/${listId}/items`, {
          title: item.title,
          assignee_person_id: item.assignee_person_id || undefined,
          due_at: item.due_at || undefined,
        });
      }
      fin();
    } finally { setBusy(false); }
  };

  const doShare = async () => {
    // Smart share — single source of truth via shareWithIntro. Routes to
    // chore vs todo template so the marketing intro fits the context
    // ("here's a chore from Kinly" / "here's a to-do from Kinly").
    if (kind === "chore") {
      await shareWithIntro({ kind: "chore", chore: item, members });
    } else {
      await shareWithIntro({ kind: "todo", todo: item, listName });
    }
    close();
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    setBusy(true);
    try { await api.delete(deleteUrl); fin(); }
    finally { setBusy(false); }
  };

  return (
    <BottomSheet open onClose={close} title={mode === "menu" ? item.title : null}>
      <div data-testid={`item-action-sheet-${item.id}`} className="pb-3">
        {mode === "menu" && (
          <ul className="divide-y divide-border/60">
            <ActionRow icon={Pencil} label="Edit title"
              testid="action-edit" onClick={() => setMode("rename")} />
            <ActionRow icon={UserCog} label="Reassign"
              testid="action-reassign" onClick={() => setMode("reassign")} />
            <ActionRow icon={CalendarClock} label="Reschedule"
              testid="action-reschedule" onClick={() => setMode("reschedule")} />
            <ActionRow icon={Copy} label="Duplicate"
              testid="action-duplicate" onClick={doDuplicate} busy={busy} />
            <ActionRow icon={Share2} label="Share"
              testid="action-share" onClick={doShare} />
            <ActionRow icon={Trash2} label="Delete" destructive
              testid="action-delete" onClick={doDelete} busy={busy} />
          </ul>
        )}

        {mode === "rename" && (
          <RenameForm initial={item.title} busy={busy}
            onCancel={() => setMode("menu")} onSubmit={doRename} />
        )}

        {mode === "reassign" && (
          <ReassignList members={members} busy={busy}
            currentId={kind === "chore"
              ? (item.assignee_person_ids || [])[0]
              : item.assignee_person_id}
            onCancel={() => setMode("menu")}
            onPick={doReassign} />
        )}

        {mode === "reschedule" && (
          <RescheduleForm initial={item.due_at} busy={busy}
            onCancel={() => setMode("menu")} onSubmit={doReschedule} />
        )}
      </div>
    </BottomSheet>
  );
}

function ActionRow({ icon: Icon, label, onClick, testid, destructive, busy }) {
  return (
    <li>
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        disabled={busy}
        className={[
          "w-full flex items-center gap-3 py-3.5 tap text-left disabled:opacity-50",
          destructive ? "text-destructive" : "text-foreground",
        ].join(" ")}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="text-[15px] font-medium">{label}</span>
        {busy && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
      </button>
    </li>
  );
}

function RenameForm({ initial, busy, onCancel, onSubmit }) {
  const [v, setV] = useState(initial || "");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (v.trim()) onSubmit(v.trim()); }}
      className="space-y-4"
      data-testid="action-rename-form"
    >
      <input
        autoFocus
        data-testid="action-rename-input"
        value={v}
        onChange={(e) => setV(e.target.value)}
        className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none text-[16px]"
      />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onCancel}
          className="h-11 rounded-2xl border border-border text-sm font-medium tap inline-flex items-center justify-center gap-1.5">
          <X className="w-4 h-4" /> Cancel
        </button>
        <button type="submit" disabled={busy || !v.trim()} data-testid="action-rename-save"
          className="h-11 rounded-2xl bg-foreground text-background text-sm font-medium tap disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>
    </form>
  );
}

function ReassignList({ members, currentId, busy, onCancel, onPick }) {
  return (
    <div className="space-y-3" data-testid="action-reassign-list">
      <button
        type="button"
        data-testid="action-reassign-anyone"
        onClick={() => onPick(null)}
        disabled={busy}
        className={[
          "w-full text-left px-3 py-2.5 rounded-xl border tap",
          !currentId ? "border-foreground bg-foreground text-background" : "border-border",
        ].join(" ")}
      >
        <span className="text-[14px] font-medium">Anyone</span>
      </button>
      <ul className="space-y-1.5">
        {members.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              data-testid={`action-reassign-${m.id}`}
              onClick={() => onPick(m.id)}
              disabled={busy}
              className={[
                "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border tap",
                currentId === m.id ? "border-foreground bg-foreground text-background" : "border-border",
              ].join(" ")}
            >
              <MemberAvatar member={m} size={24} ring={false} />
              <span className="text-[14px] font-medium">{m.display_name}</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" onClick={onCancel}
        className="w-full h-10 rounded-xl border border-border text-sm font-medium tap">
        Back
      </button>
    </div>
  );
}

function RescheduleForm({ initial, busy, onCancel, onSubmit }) {
  const seed = initial ? new Date(initial) : new Date(new Date().setHours(9, 0, 0, 0));
  const pad = (n) => String(n).padStart(2, "0");
  const [date, setDate] = useState(localYmd(seed));
  const [time, setTime] = useState(`${pad(seed.getHours())}:${pad(seed.getMinutes())}`);

  const submit = (e) => {
    e.preventDefault();
    const iso = new Date(`${date}T${time}:00`).toISOString();
    onSubmit(iso);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="action-reschedule-form">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Date</span>
          <input
            type="date"
            data-testid="action-reschedule-date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full bg-card border border-border rounded-xl px-3 py-2 mt-1 outline-none text-[15px]"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Time</span>
          <input
            type="time"
            data-testid="action-reschedule-time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full bg-card border border-border rounded-xl px-3 py-2 mt-1 outline-none text-[15px]"
          />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onCancel}
          className="h-11 rounded-2xl border border-border text-sm font-medium tap inline-flex items-center justify-center gap-1.5">
          <X className="w-4 h-4" /> Cancel
        </button>
        <button type="submit" disabled={busy} data-testid="action-reschedule-save"
          className="h-11 rounded-2xl bg-foreground text-background text-sm font-medium tap disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>
    </form>
  );
}
